import classNames from 'classnames'
import InfoElement from 'components/InfoElement'
import React, { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { emptyArray, formatNumberWithCurrency, getDifferenceWithPrecision } from 'utils'

import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'

import type { DetailsProps } from './types'
import useStyles from './useStyles'
import { getMissingDataText } from './utils'

// eslint-disable-next-line sonarjs/cognitive-complexity
function GeneralRevenueDetails({ data, description, headers = emptyArray, showLastYear = false }: DetailsProps) {
  const { t: translate } = useTranslation()
  const classes = useStyles()

  if (!data || Object.keys(data).length === 0) {
    return null
  }
  const showOrders = (showLastYear ? data.storesComparedECom : data.storesCurrentECom) > 0
  const stationaryData = [
    {
      icon: <div className="fm fm-shop" />,
      count: showLastYear ? data.storesComparedNECom : data.storesCurrentNECom,
      countActive: showLastYear ? data.storesComparedNEComActive : data.storesCurrentNEComActive,
      revenue: showLastYear ? data.revenueComparedNECom : data.revenueCurrentNECom,
      revenueCompared: showLastYear ? undefined : data.revenueComparedNECom,
      revenuePercent: showLastYear ? undefined : data.revenuePercentNECom,
      average: showLastYear ? data.revenuePerStoreComparedNECom : data.revenuePerStoreCurrentNECom,
      revenuePerStore: showLastYear ? data.revenuePerStoreComparedNECom : data.revenuePerStoreCurrentNECom,
      revenuePerStorePercent: showLastYear ? undefined : data.revenuePerStorePercentNECom,
      missingDataText: 'revenue.noOpenStationaryStores',
    },
    {
      icon: <i className="fm fm-l4l" />,
      count: showLastYear ? data.storesComparedL4L : data.storesCurrentL4L,
      countActive: showLastYear ? data.storesComparedL4LActive : data.storesCurrentL4LActive,
      revenue: showLastYear ? data.revenueComparedL4L : data.revenueCurrentL4L,
      revenueCompared: showLastYear ? undefined : data.revenueComparedL4L,
      revenuePercent: showLastYear ? undefined : data.revenuePercentL4L,
      average: showLastYear ? data.revenuePerStoreComparedL4L : data.revenuePerStoreCurrentL4L,
      revenuePerStore: showLastYear ? data.revenuePerStoreComparedL4L : data.revenuePerStoreCurrentL4L,
      revenuePerStorePercent: showLastYear ? undefined : data.revenuePerStorePercentL4L,
      missingDataText: 'revenue.noComparableStoresOpen',
    },
    {
      icon: <i className="fm fm-surfchange-up" />,
      count: showLastYear ? data.storesComparedFSI : data.storesCurrentFSI,
      countActive: showLastYear ? data.storesComparedFSIActive : data.storesCurrentFSIActive,
      revenue: showLastYear ? data.revenueComparedFSI : data.revenueCurrentFSI,
      revenueCompared: showLastYear ? undefined : data.revenueComparedFSI,
      revenuePercent: showLastYear ? undefined : data.revenuePercentFSI,
      average: showLastYear ? data.revenuePerStoreComparedFSI : data.revenuePerStoreCurrentFSI,
      revenuePerStore: showLastYear ? data.revenuePerStoreComparedFSI : data.revenuePerStoreCurrentFSI,
      revenuePerStorePercent: showLastYear ? undefined : data.revenuePerStorePercentFSI,
      missingDataText: 'revenue.noOpenStoresWithFloorIncrease',
    },
    {
      icon: <i className="fm fm-surfchange-down" />,
      count: showLastYear ? data.storesComparedFSD : data.storesCurrentFSD,
      countActive: showLastYear ? data.storesComparedFSDActive : data.storesCurrentFSDActive,
      revenue: showLastYear ? data.revenueComparedFSD : data.revenueCurrentFSD,
      revenueCompared: showLastYear ? undefined : data.revenueComparedFSD,
      revenuePercent: showLastYear ? undefined : data.revenuePercentFSD,
      average: showLastYear ? data.revenuePerStoreComparedFSD : data.revenuePerStoreCurrentFSD,
      revenuePerStore: showLastYear ? data.revenuePerStoreComparedFSD : data.revenuePerStoreCurrentFSD,
      revenuePerStorePercent: showLastYear ? undefined : data.revenuePerStorePercentFSD,
      missingDataText: 'revenue.noOpenStoresWithFloorDecrease',
    },
  ]

  const rows = []

  if (data.anyStoresNECom !== false) {
    rows.push(...stationaryData)
  }

  rows.push({
    icon: <i className="fm fm-at" />,
    count: showLastYear ? data.storesComparedECom : data.storesCurrentECom,
    countActive: showLastYear ? data.storesComparedEComActive : data.storesCurrentEComActive,
    revenue: showLastYear ? data.revenueComparedECom : data.revenueCurrentECom,
    revenueCompared: showLastYear ? undefined : data.revenueComparedECom,
    revenuePercent: showLastYear ? undefined : data.revenuePercentECom,
    average: showLastYear ? data.transactionValueComparedECom : data.transactionValueCurrentECom,
    revenuePerStorePercent: showLastYear ? undefined : data.transactionValuePercentECom,
    missingDataText: 'revenue.noOnlineReportingStores',
  })

  if (showOrders) {
    rows.push({
      icon: <i className="fm fm-order-checked" />,
      count: showLastYear ? data.storesComparedWithOrders : data.storesCurrentWithOrders,
      countActive: showLastYear ? data.storesComparedWithOrdersActive : data.storesCurrentWithOrdersActive,
      revenue: showLastYear ? data.ordersValueCompared : data.ordersValueCurrent,
      revenueCompared: showLastYear ? undefined : data.ordersValueCompared,
      revenuePercent: showLastYear ? undefined : data.ordersValuePercent,
      average: showLastYear ? data.orderValueCompared : data.orderValueCurrent,
      revenuePerStore: showLastYear ? data.orderValueCompared : data.orderValueCurrent,
      revenuePerStorePercent: showLastYear ? undefined : data.orderValuePercent,
      missingDataText: 'revenue.noOrdersToEcom',
    })
  }
  const [count, revenue, avg] = headers

  return (
    <div className="general-revenue_details">
      {description && <div className={classes.description}>{description}</div>}

      <Grid container>
        <Grid container>
          <Grid item xs={2} className={classNames(classes.header, classes.cell)}>
            {count}
          </Grid>
          <Grid item xs={5} className={classNames(classes.header, classes.cell)}>
            {revenue}
          </Grid>
          <Grid item xs={5} className={classes.header}>
            {avg}
          </Grid>
        </Grid>

        {rows.map((rowData, index) => {
          const hasAverage = rowData.average || rowData.average === 0
          const showRevenue = rowData.revenue || rowData.revenue > 0
          const missingDataText = getMissingDataText(rowData.missingDataText, rowData, translate)

          return (
            <Fragment key={rowData.icon.props.className}>
              <Box width="100%">
                <Divider />
              </Box>

              <Grid container>
                <Grid item xs={1} className={classes.icon}>
                  {rowData.icon}
                </Grid>
                <Grid item xs={1} className={classNames(classes.cell, classes.value)}>
                  {rowData.count || '-'}
                </Grid>

                {showRevenue ? (
                  <>
                    <Grid item xs={3} className={classes.value}>
                      {formatNumberWithCurrency({
                        currency: data.currency,
                        number: rowData.revenue,
                        translate,
                      })}
                    </Grid>
                    <Grid item xs={2} className={classes.revenueValue} display="flex" alignItems="center">
                      {!showLastYear && (
                        <InfoElement
                          className={classNames(classes.value, classes.marginLeft)}
                          percentDifference={getDifferenceWithPrecision({
                            handleNullCase: true,
                            number: rowData.revenuePercent,
                            precision: 0,
                            translate,
                          })}
                        />
                      )}
                    </Grid>
                    <Grid item xs={3} className={classes.value}>
                      {hasAverage &&
                        formatNumberWithCurrency({
                          currency: data.currency,
                          number: rowData.average,
                          translate,
                        })}
                    </Grid>
                    <Grid item xs={2} display="flex" alignItems="center">
                      {hasAverage && !showLastYear && (
                        <InfoElement
                          className={classNames(classes.value, classes.marginLeft)}
                          percentDifference={getDifferenceWithPrecision({
                            handleNullCase: true,
                            number: rowData.revenuePerStorePercent,
                            precision: 0,
                            translate,
                          })}
                        />
                      )}
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={10} className={classes.missingData}>
                    {missingDataText}
                  </Grid>
                )}
              </Grid>
            </Fragment>
          )
        })}
        <Box width="100%">
          <Divider />
        </Box>
      </Grid>
    </div>
  )
}

export default GeneralRevenueDetails
